<template>
  <!-- Site and station edit -->
  <Form
    id="payment-info-form"
    :form-id="FinancialFormIDs.PAYMENT_INFO_FORM"
    :schema="schema"
    :initialValues="initialValues"
    @onValidateSuccess="handleValidationSuccess"
    @onValidateFail="handleValidationFail"
    ref="formComponent"
  >
    <template v-slot="{ isFormValid, form }">
      <Grid :isSiteConfig="isSiteConfig" />
    </template>
  </Form>
</template>

<script setup lang="ts">
import { Form } from '@centric-os/components/src/composed/forms/form';
import useGlobalToast from '@centric-os/components/src/core/messages/toast/composables/use-global-toast';
import { usePaymentInfo } from './composables';
import Grid from './grid/Grid.vue';
import { useTranslation } from '@centric-os/helpers';
import type { ApiResponseError, SitePrivateConfig, PaymentConfig } from '@centric-os/types';
import { computed, onUnmounted, provide, ref, toRef } from 'vue';
import { useEventBus } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { SitesLocalePrefix } from '@centric-os/sites/src/enums';
import {
  useFinancialConfigStore,
  usePaymentInfoStore,
  useSiteConfigStore,
  useStationConfigStore,
  useStationStore,
} from '../../../../routes';
import { usePaymentInfoSchema } from '../../../../composables';
import { FinancialFormIDs } from '../enums';
import { useRoute } from 'vue-router';
import { StationAppConfigFormEventKey } from '@centric-os/sites/src/routes/app-config/components/shared/enums/form-event-keys.ts';
import { paymentInfoContextKey } from './context';

const props = defineProps({
  isSiteConfig: {
    type: Boolean,
    required: true,
  },
  isCentricHub: {
    type: Boolean,
    default: false,
  },
});
provide(paymentInfoContextKey, {
  // @ts-expect-error FIXME: Type 'Ref<boolean>' is missing the following properties from type 'ComputedRe...
  isCentricHub: toRef(props, 'isCentricHub'),
});

const stationStore = useStationStore();
const { stationBrand } = storeToRefs(stationStore);

// Set localization
const { translate } = useTranslation();
const LOCALE_PREFIX = SitesLocalePrefix.SITE_PAYMENT_INFO;

// Set financial config store
const fConfigStore = useFinancialConfigStore();
const { editPaymentInfo } = storeToRefs(fConfigStore);

// Set payment info store
const paymentInfoStore = usePaymentInfoStore();
const { isPaymentInfoValid } = storeToRefs(paymentInfoStore);

const siteConfigStore = useSiteConfigStore();
const { sitePrivateConfig } = storeToRefs(siteConfigStore);

const stationConfigStore = useStationConfigStore();
const { stationPrivateConfig } = storeToRefs(stationConfigStore);

// Set payment info form schema
const { schema } = usePaymentInfoSchema();

// Set payment info composable
const {
  initialValues,
  mapPrivateConfigPayload,
  mapCentricHubPaymentPayload,
  clearCentricHubPaymentPayload,
} = usePaymentInfo({
  isCentricHub: toRef(props, 'isCentricHub'),
});

// Set global toast
const { showErrorToast, showSuccessToast } = useGlobalToast();

// Define refs for form component
const formComponent = ref<InstanceType<typeof Form>>();

// Submit form
const save = async (): Promise<void> => {
  formComponent.value?.validate();
};

// Handle validation success
const eventBusKey = `save-payment-info-${props.isSiteConfig ? 'site' : 'station'}`;
const { on: onPaymentInfo, off: offPaymentInfo } = useEventBus<void>(eventBusKey);
// Set the listener for validate and submit form
onPaymentInfo(save);

// Handle payment settings removal (only for station config)
const { on: onPaymentSettingsRemoved, off: offPaymentSettingsRemoved } = useEventBus<void>(
  StationAppConfigFormEventKey.PaymentSettingsRemovedStation,
);
const route = useRoute();

const persistPaymentConfig = async (payload: SitePrivateConfig, id: string): Promise<void> => {
  try {
    await paymentInfoStore.savePaymentInfo(id, props.isSiteConfig, payload);

    if (props.isSiteConfig) {
      await siteConfigStore.fetchSiteConfigurations(id);
    } else {
      await stationConfigStore.getStationPrivateConfig(id, true);
    }

    showSuccessToast(translate(`${LOCALE_PREFIX}.form.success`));
    isPaymentInfoValid.value = false;
    editPaymentInfo.value = false;
  } catch (error: unknown) {
    const err = error as ApiResponseError;
    if (err.status) {
      if (err.status >= 400 && err.status < 500) {
        showErrorToast(err?.error ? err?.error : (err?.message as string));
      } else {
        showErrorToast(translate('errorMessages.genericApiError'));
      }
    }
  }
};

// Set the listener for payment settings removal
const removePaymentSettings = async (): Promise<void> => {
  formComponent.value?.form?.resetForm();

  if (props.isCentricHub) {
    const id = route.params.stationId as string;
    const payload = clearCentricHubPaymentPayload(stationPrivateConfig.value);
    await persistPaymentConfig(payload, id);
    return;
  }

  stationPrivateConfig.value.payment = undefined;
  stationPrivateConfig.value.keystore = {
    ...stationPrivateConfig.value?.keystore,
    payment: undefined,
  };

  const id = route.params.stationId as string;
  const payload = mapPrivateConfigPayload({} as PaymentConfig, stationPrivateConfig.value);
  await persistPaymentConfig(payload, id);

  if (!stationBrand.value.is.hidden) {
    // stations without payment config should be hidden
    stationBrand.value.is.hidden = true;
    await stationStore.updateStationBrand(stationBrand.value);
    showSuccessToast(translate(`${LOCALE_PREFIX}.form.stationDisabled`));
  }
};

onPaymentSettingsRemoved(removePaymentSettings);

const handleValidationSuccess = async (form: Partial<PaymentConfig>): Promise<void> => {
  let payload: SitePrivateConfig = null;
  let id: string = null;

  if (props.isCentricHub) {
    id = route.params.stationId as string;
    payload = mapCentricHubPaymentPayload(form, stationPrivateConfig.value);
  } else if (props.isSiteConfig) {
    id = route.params.siteId as string;
    payload = mapPrivateConfigPayload(form, sitePrivateConfig.value);
  } else {
    id = route.params.stationId as string;
    payload = mapPrivateConfigPayload(form, stationPrivateConfig.value);
  }

  await persistPaymentConfig(payload, id);
};

// Handle validation fail
const handleValidationFail = (): void => {
  showErrorToast(translate(`${LOCALE_PREFIX}.form.validationFailed`));
};

// Unmount listeners
onUnmounted(() => {
  offPaymentInfo(save);
  offPaymentSettingsRemoved(removePaymentSettings);
  editPaymentInfo.value = false;
});
</script>
